/**
 * F17.2.7 — Pannello composition controsoffitto con drag&drop + zone semantiche.
 *
 * Analogo a `LayersPanel.tsx` (wall) ma adattato al dominio ceiling:
 *  - Header "Camera tecnica" (info display) — drop + pendinatura
 *  - Zone semantiche (TOP→BOTTOM):
 *      1. Orditura primaria (primo profilo + eventuale isolante adiacente above)
 *      2. Orditura secondaria (secondo profilo, solo se doppia)
 *      3. Pacchetto a vista (board / ceiling_tile / pannelli)
 *  - Controlli dimensionali integrati: toggle pendinato, drop, hanger spacing,
 *    primary spacing, secondary spacing.
 *  - Riusa LayerCard, MaterialPickerDialog, CertificationPanel.
 *  - dnd-kit per riordinamento (identico al wall LayersPanel).
 *
 * NON ha:
 *  - Match certificato (il banner CertifiedMatchBanner è specifico per pareti)
 *  - Suggestion auto montante↔guida (logica ceiling diversa)
 *
 * Sostituisce `CeilingComposeShell` (placeholder MVP della F17.2.6).
 */
import React, { useMemo, useState } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Plus, Anchor, Ruler, AlertCircle,
} from 'lucide-react';
import { useCanWrite } from '@/hooks/useCanWrite';
import { WriteBlockedTooltip } from '@/components/billing/UpsellTooltip';
import LayerCard from './LayerCard';
import CertificationPanel from './CertificationPanel';
import MaterialPickerDialog from '../material-picker/MaterialPickerDialog';
import { useScrewSuggestions } from '../hooks/useScrewSuggestions';
import type { CertificationData } from '../hooks/useConfiguratorState';
import type { CeilingDimensionalState } from '../hooks/useCeilingConfiguratorState';
import type { LayerV2 } from '../types';
import type { DatabaseMaterial, MaterialCategory } from '@/hooks/useMaterials';

interface CeilingLayersPanelProps {
  layers: LayerV2[];
  ceiling: CeilingDimensionalState;
  onAddLayer: (initial?: Partial<LayerV2>) => string;
  onUpdateLayer: (id: string, patch: Partial<LayerV2>) => void;
  onRemoveLayer: (id: string) => void;
  onDuplicateLayer: (id: string) => string | null;
  onReorderLayers: (orderedIds: string[]) => void;
  onSetMaterial: (layerId: string, material: DatabaseMaterial) => void;
  onSetScrewMaterial: (layerId: string, screw: DatabaseMaterial | null) => void;
  onSetScrewQuantity: (layerId: string, qty: number) => void;
  onReapplyScrewSuggestion: (layerId: string) => void;
  onSetCeiling: (patch: Partial<CeilingDimensionalState>) => void;
  isCertified: boolean;
  certification: CertificationData;
  onSetCertification: (patch: Partial<CertificationData> & { isCertified?: boolean }) => void;
}

const CeilingLayersPanel: React.FC<CeilingLayersPanelProps> = ({
  layers,
  ceiling,
  onAddLayer,
  onUpdateLayer,
  onRemoveLayer,
  onDuplicateLayer,
  onReorderLayers,
  onSetMaterial,
  onSetScrewMaterial,
  onSetScrewQuantity,
  onReapplyScrewSuggestion,
  onSetCeiling,
  isCertified,
  certification,
  onSetCertification,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLayerId, setPickerLayerId] = useState<string | null>(null);
  const [pickerCategory, setPickerCategory] = useState<MaterialCategory | 'all'>('all');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const { canWrite } = useCanWrite();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = layers.findIndex(l => l.id === active.id);
    const newIndex = layers.findIndex(l => l.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(layers, oldIndex, newIndex);
    onReorderLayers(reordered.map(l => l.id));
  };

  const openPickerForLayer = (layerId: string, layer: LayerV2) => {
    setPickerLayerId(layerId);
    setPickerCategory((layer.material?.category as MaterialCategory) ?? 'all');
    setPickerOpen(true);
  };

  /** Quick-add: aggiunge un layer vuoto + apre picker con category pre-selezionata. */
  const openPickerForNew = (categoryHint: MaterialCategory | 'all' = 'all') => {
    const newLayerId = onAddLayer({ thickness: categoryHint === 'board' ? 12.5 : 0 });
    setPickerLayerId(newLayerId);
    setPickerCategory(categoryHint);
    setPickerOpen(true);
  };

  const handlePickerSelect = (material: DatabaseMaterial) => {
    if (pickerLayerId) onSetMaterial(pickerLayerId, material);
    setPickerLayerId(null);
  };

  // Suggestions viti per board / ceiling_tile
  const { suggestions: screwSuggestions } = useScrewSuggestions(layers, 'ceiling');

  // Zone semantiche ceiling-specific
  const zonedEntries = useMemo(() => zoneLayersForCeiling(layers), [layers]);

  const isSuspended = ceiling.attachmentType === 'suspended';
  const hasSecondary = ceiling.secondarySpacingMm !== null;

  return (
    <>
      <div className="space-y-4">
        {/* === BOX: Camera tecnica + pendinatura === */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Anchor className="h-4 w-4" />
              Sospensione al solaio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-muted/30">
              <div className="min-w-0">
                <div className="text-sm font-medium">
                  {isSuspended ? 'Pendinato' : 'A contatto con solaio'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {isSuspended
                    ? 'Sospeso con pendini e camera tecnica'
                    : 'Fissato direttamente al solaio (no pendinatura)'}
                </div>
              </div>
              <Switch
                checked={isSuspended}
                disabled={!canWrite}
                onCheckedChange={(checked) =>
                  onSetCeiling({
                    attachmentType: checked ? 'suspended' : 'fixed',
                    ceilingDropMm: checked ? (ceiling.ceilingDropMm || 200) : 0,
                  })
                }
              />
            </div>

            {isSuspended && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="drop" className="text-xs">Altezza pendinatura (mm)</Label>
                  <Input
                    id="drop"
                    type="number"
                    min="50"
                    max="2000"
                    step="10"
                    disabled={!canWrite}
                    value={ceiling.ceilingDropMm}
                    onChange={(e) => onSetCeiling({ ceilingDropMm: parseInt(e.target.value) || 200 })}
                  />
                  {ceiling.ceilingDropMm > 500 && (
                    <div className="flex items-center gap-1 text-[10px] text-amber-600 mt-1">
                      <AlertCircle className="h-3 w-3" />
                      Tecnico — verifica portata pendini
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="hanger-primary" className="text-xs">Passo pendini (mm)</Label>
                  <Input
                    id="hanger-primary"
                    type="number"
                    min="300"
                    max="1500"
                    step="50"
                    disabled={!canWrite}
                    value={ceiling.hangerSpacingPrimaryMm}
                    onChange={(e) => onSetCeiling({ hangerSpacingPrimaryMm: parseInt(e.target.value) || 900 })}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* === BOX: Orditura === */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              Orditura
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="primary-spacing" className="text-xs">
                  Passo orditura primaria (mm)
                </Label>
                <Input
                  id="primary-spacing"
                  type="number"
                  min="300"
                  max="1500"
                  step="50"
                  disabled={!canWrite}
                  value={ceiling.primarySpacingMm}
                  onChange={(e) => onSetCeiling({ primarySpacingMm: parseInt(e.target.value) || 1000 })}
                />
              </div>
              <div>
                <Label htmlFor="secondary-spacing" className="text-xs flex items-center gap-1">
                  Secondaria (mm)
                  <Badge variant="outline" className="text-[9px]">opz</Badge>
                </Label>
                <Input
                  id="secondary-spacing"
                  type="number"
                  min="300"
                  max="1000"
                  step="50"
                  disabled={!canWrite}
                  value={ceiling.secondarySpacingMm ?? ''}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    onSetCeiling({ secondarySpacingMm: v ? parseInt(v) || 500 : null });
                  }}
                  placeholder="solo doppia"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="perimeter-ratio" className="text-xs flex items-center gap-1">
                Profilo perimetrale (ml/m²)
                <Badge variant="outline" className="text-[9px]">avanzato</Badge>
              </Label>
              <Input
                id="perimeter-ratio"
                type="number"
                min="0"
                max="2"
                step="0.05"
                disabled={!canWrite}
                value={ceiling.perimeterProfileMlPerSqm ?? ''}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  onSetCeiling({ perimeterProfileMlPerSqm: v ? parseFloat(v) : null });
                }}
                placeholder="vuoto = 4·√area al preventivo"
              />
            </div>
          </CardContent>
        </Card>

        {/* === Quick-add bar (3 categorie tipiche ceiling) === */}
        <div className="grid grid-cols-3 gap-2">
          {!canWrite ? (
            <WriteBlockedTooltip action="Aggiungi layer" side="top">
              <span className="col-span-3 block">
                <Button disabled variant="default" className="w-full h-10 gap-1">
                  <Plus className="h-4 w-4" /> Aggiungi layer
                </Button>
              </span>
            </WriteBlockedTooltip>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-10 gap-1 text-xs"
                onClick={() => openPickerForNew('structure_frame')}
              >
                <Plus className="h-3 w-3" /> Orditura
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-10 gap-1 text-xs"
                onClick={() => openPickerForNew('insulation')}
              >
                <Plus className="h-3 w-3" /> Isolante
              </Button>
              <Button
                variant="default"
                size="sm"
                className="h-10 gap-1 text-xs"
                onClick={() => openPickerForNew('board')}
              >
                <Plus className="h-3 w-3" /> Lastra / Pannello
              </Button>
            </>
          )}
        </div>

        {/* === Lista layer con drag&drop e zone semantiche === */}
        {layers.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted-foreground border-dashed">
            Nessun layer ancora.<br />
            Inizia da <span className="font-medium">Orditura</span> per impostare la struttura,
            poi aggiungi isolante e lastre.
          </Card>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={layers.map(l => l.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {zonedEntries.map((entry, idx) => {
                  if (entry.kind === 'divider') {
                    return (
                      <div
                        key={`div-${idx}`}
                        className="flex items-center gap-2 pt-3 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold"
                      >
                        <div className="flex-1 border-t border-zinc-200" />
                        <span>{entry.label}</span>
                        <div className="flex-1 border-t border-zinc-200" />
                      </div>
                    );
                  }
                  return (
                    <LayerCard
                      key={entry.layer.id}
                      layer={entry.layer}
                      screwSuggestion={screwSuggestions.get(entry.layer.id)}
                      onUpdate={(patch) => onUpdateLayer(entry.layer.id, patch)}
                      onRemove={() => onRemoveLayer(entry.layer.id)}
                      onDuplicate={() => onDuplicateLayer(entry.layer.id)}
                      onClickMaterial={() => openPickerForLayer(entry.layer.id, entry.layer)}
                      onChangeScrew={(s) => onSetScrewMaterial(entry.layer.id, s)}
                      onChangeScrewQty={(q) => onSetScrewQuantity(entry.layer.id, q)}
                      onReapplyScrewSuggestion={() => onReapplyScrewSuggestion(entry.layer.id)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Certificazione (riusato 1:1 dal wall) */}
        {layers.length > 0 && (
          <CertificationPanel
            isCertified={isCertified}
            certification={certification}
            onChange={onSetCertification}
          />
        )}

        {/* Footer info */}
        {layers.length > 0 && (
          <div className="text-[11px] text-muted-foreground text-center pt-2">
            Configurazione: <strong>{isSuspended ? 'Pendinato' : 'A contatto'}</strong>
            {hasSecondary ? ' · orditura doppia' : ' · orditura singola'}
          </div>
        )}
      </div>

      <MaterialPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialCategory={pickerCategory}
        onSelect={handlePickerSelect}
      />
    </>
  );
};

// ============================================================================
// Zone semantiche ceiling (TOP→BOTTOM seguendo l'ordine dei layer)
// ============================================================================

type ZonedEntry =
  | { kind: 'divider'; label: string }
  | { kind: 'layer'; layer: LayerV2 };

/**
 * Inserisce DIVIDER tra i layer per riflettere la struttura ceiling:
 *  - 1° profilo trovato (structure_frame/guide) → "Orditura primaria"
 *  - Insulation prima del 2° profilo → resta in "Orditura primaria"
 *  - 2° profilo → "Orditura secondaria" (se presente)
 *  - Insulation dopo il 2° profilo → resta in "Orditura secondaria"
 *  - 1° board/ceiling_tile → "Pacchetto a vista"
 *  - Layer accessory/other → "Accessori" alla fine
 */
function zoneLayersForCeiling(layers: LayerV2[]): ZonedEntry[] {
  if (layers.length === 0) return [];

  const entries: ZonedEntry[] = [];
  let frameSeen = 0;
  let primarySectionOpen = false;
  let secondarySectionOpen = false;
  let boardSectionOpen = false;

  for (const layer of layers) {
    const cat = layer.material?.category;
    const isProfile = cat === 'structure_frame' || cat === 'structure_guide';
    const isBoard = cat === 'board' || cat === 'ceiling_tile';

    if (isProfile) {
      if (frameSeen === 0) {
        entries.push({ kind: 'divider', label: 'Orditura primaria' });
        primarySectionOpen = true;
      } else if (frameSeen === 1) {
        entries.push({ kind: 'divider', label: 'Orditura secondaria' });
        primarySectionOpen = false;
        secondarySectionOpen = true;
      }
      // 3° profilo (raro): continua nella secondaria
      frameSeen++;
    } else if (isBoard) {
      if (!boardSectionOpen) {
        entries.push({ kind: 'divider', label: 'Pacchetto a vista' });
        primarySectionOpen = false;
        secondarySectionOpen = false;
        boardSectionOpen = true;
      }
    } else if (cat === 'insulation') {
      // Insulation senza divider: rimane nella sezione corrente (primary/secondary).
      // Se è prima di qualsiasi profilo, apre "Sotto solaio" (caso raro)
      if (frameSeen === 0 && !primarySectionOpen) {
        entries.push({ kind: 'divider', label: 'Strato sotto solaio' });
        primarySectionOpen = true;
      }
    } else {
      // accessory / screw / finish / other → "Altri"
      if (!boardSectionOpen) {
        // Se sono in mezzo all'orditura, OK lasciali lì
      } else {
        // Già in board section: ok
      }
    }

    entries.push({ kind: 'layer', layer });
  }

  return entries;
}

export default CeilingLayersPanel;
